export default {
  caseStudy: {
    viewFullCase: 'ビジュアルデータと詳細を見る',
    readOnlyBadge: '実際の事例・読み取り専用サンプル',
    backToLanding: 'ホームに戻る',
    startOwnAnalysis: '自分の写真を分析',
    uploadMyPhoto: '私の写真で試してみる',
    pageTitle: '建築 - PhotoScope 実際の事例分析',
    pageDescription:
      'PhotoScope がトーン、色、ムードをどう分解し、アップロード写真と参考建築スタイルの差を埋めるかをご覧ください。',
    notFoundTitle: '事例が見つかりません',
    notFoundDescription: 'お探しの事例は存在しないか、提供を終了しました。',
    loadFailedTitle: '事例の読み込みに失敗しました',
    loadFailedDescription: '事例の読み込み中に問題が発生しました。再度お試しください。',
    retry: '再試行',
  },
  uploadShowcase: {
    title: '実際の分析例を見る',
  },
  heroB: {
    comparison: {
      rawAlt: '比較画像 1',
      referenceAlt: '比較画像 2',
    },
    gaps: {
      gap_1: {
        title: '露出の配置と中間調の持ち上げ',
        brief: '元画像は全体に暗めに置かれており、中間調の帯に残る情報が少なめです。',
        reason: 'この差は画面全体のトーンの読み取りを左右するため、より繊細な色作業を説得力あるものにするには、まず解消しておく必要があります。',
      },
      gap_2: {
        title: '黒つぶれとシャドウの圧縮',
        brief: '元画像は黒に近い領域に情報が寄りすぎており、光が硬く閉じた印象になっています。',
        reason: '重い黒への固定は、元画像がまだ参考画像の空気感を共有できていない主な理由のひとつです。',
      },
      gap_3: {
        title: 'ホワイトバランスの軸と空気感の偏り',
        brief: '元画像はマゼンタ寄りの青に傾いていますが、参考画像はより暖かい黄緑の空気感を土台にしています。',
        reason: 'これはカラーパレットの軸そのものに関わる根本的な差であり、特定の被写体や領域ではなく、画面全体のムードを変えてしまいます。',
      },
      gap_4: {
        title: 'パレットの分離と空／壁面の質感',
        brief: '元画像は鮮烈な青とテラコッタの対比を保っていますが、参考画像はより柔らかな石材色とシアンのパレットです。',
        reason: 'この差は、主要なトーンとカブリの問題を解決したあとに行うスタイルの仕上げであり、最終的な仕上がりの近さに強く影響します。',
      },
    },
    aiGuideMd: `説得力のあるマッチングは、核となる差を同じ順序でたどることから生まれます。まず**露出の配置と中間調の持ち上げ**、次に**黒つぶれとシャドウの圧縮**、続いて**ホワイトバランスの軸と空気感の偏り**、最後に**パレットの分離と空／壁面の質感**です。元画像と参考画像はそもそも別のシーンなので、この街並みに存在しない色を作り出すのではなく、トーンの論理とパレットの振る舞いを合わせることを目指してください。

1. **まず「露出の配置と中間調の持ち上げ」で全体の明るさを決めます。**「基本」パネルで、**露光量 +0.55〜+0.80**、**ハイライト -15〜-30**、**シャドウ +35〜+55**、**白レベル +8〜+18**、**黒レベル +18〜+30** から始めます。狙いは、現状の暗い都市的なトーンから、より明るい中間調主導の配置へと画面を移すことです。日の当たる壁面と右側の淡い壁に注目してください。壁が形を失い始めたら、露光量より先に白レベルを下げます。

2. **「黒つぶれとシャドウの圧縮」に合わせてコントラストを作り直します。**露出が定まったら、**コントラスト -10〜-20** を下げます。その後は基本パネルのスライダーで押し込むのではなく、緩やかなポイントカーブを使います。始点となるカーブの目安は、黒点を **(0,0)** から **(0,10)** へ、シャドウ下側のポイントを **(40,32)〜(40,42)** 付近、中間調のポイントを **(128,128)〜(128,138)** 付近、ハイライト上側のポイントを **(210,210)〜(210,214)** 付近に置きます。これで黒レベルが持ち上がり、中央が開き、明部は制御されたままになります。眠く平板に見えてきたら、黒点を再び下げるのではなく、中間調のポイントで少しだけ分離を戻します。

3. **「ホワイトバランスの軸と空気感の偏り」に合わせて全体のカブリを補正します。**元画像は、より暖かく、わずかに緑寄りへ動かす必要があります。「カラー」パネルで、**色温度 +8〜+14**、**色かぶり補正 -8〜-15** を試します。これは、冷たいマゼンタ寄りの青い都市的な見えから、参考画像の暖かな石材と空気のバランスへと近づける鍵となる工程です。テラコッタの壁面が急に黄色くなりすぎるなら、まず色温度を下げ、より多くの補正を色かぶり側に残します。

4. **「パレットの分離と空／壁面の質感」のために、図案的な青—オレンジの対比を弱めます。**HSL に入る前に、抑えめの全体的な色コントロールを使います。**自然な彩度 -8〜-18**、**彩度 -2〜-8**。参考画像は無彩色ではありませんが、補色対比がそれほど強烈でも人工的でもありません。日の当たる建物には局所的な暖かさを十分に残し、画面が自然光で照らされているように読めるようにします。

5. **「カラーミキサー」で、主に青とオレンジを対象にパレットを精緻化します。**空には、**青 色相 -20〜-35**、**青 彩度 -20〜-35**、**青 輝度 +10〜+22** を使います。これで深いコバルトブルーの空が柔らかなシアンへと移り、重いデジタル感が和らぎます。日の当たる壁面には、**オレンジ 色相 +8〜+18**、**オレンジ 彩度 -10〜-20**、**オレンジ 輝度 +8〜+18** を使います。テラコッタがまだ赤すぎるなら、**赤 彩度 -8〜-15** を加えます。これらの操作で、現状の強い絵葉書のような青—オレンジの対比が、より柔らかな石材のような暖かさに変わります。副作用として窓や金属のディテールが生気を失うことがあるので、中性色が白っぽく粉を吹いたようになる前に止めてください。

6. **暖色／寒色の関係をまとめるために、控えめなカラーグレーディングを加えます。**この工程は軽く保ちます。**中間調 色相 42〜50 / 彩度 8〜12**、**シャドウ 色相 52〜60 / 彩度 4〜7**、**ハイライト 色相 190〜200 / 彩度 4〜8**、そして**ブレンド 60〜70**、**バランス +5〜+15** を試します。目的は分かりやすいスプリットトーンではなく、画面の建築本体を暖めつつ、明るい空気をわずかに冷たく保つことです。右側の影の壁が濁ったり緑がかったりしたら、まずシャドウの彩度を戻します。

7. **全体の調色では届かない部分は、局所マスクで解決します。**右側の影の壁と主建物の下部の暗い部分に、ブラシまたは線形グラデーションのマスクを作り、おおよそ**露光量 +0.30〜+0.55**、**シャドウ +25〜+40**、**黒レベル +10〜+18**、**彩度 -5〜-10** とします。これで影を、どこからともなく照らされた印象にせずに読めるまま保てます。空には、空マスクを使い、**彩度 -8〜-18**、**かすみの除去 -4〜-10**、必要なら**露光量 +0.05〜+0.15** を加えます。狙いは、劇的な空ではなく、より明るく穏やかな空の面です。

8. **最後に質感を整え、画面の硬さを和らげます。**全体に**テクスチャ -5〜-10**、**明瞭度 -5〜-12** を適用し、それでもまだシャープすぎるなら**かすみの除去 -2〜-6** を加えます。少量の**粒子 適用量 10〜18**、**サイズ 20〜30**、**粗さ 40〜55** は、パレットをより有機的に見せる助けになります。エッジが建築的な輪郭を失いすぎたら、コントラストを戻す前に、まず明瞭度を少しだけ戻します。

主なチェックポイントはシンプルです。調整後も元画像は、街の谷間で日に照らされた建築として読めなければなりませんが、中間調はより明るく、黒はより軽く、石材はより暖かく、空の青はそれほど押し出しが強くない状態になっているはずです。そこに到達できれば、無理のある変換を強いることなく、仕上がりは参考画像のムードにずっと近づきます。`,
  },
  advantagesB: {
    title: "ただのフィルターでも、ただのグラフ集でもありません",
    items: {
      styleLearning: {
        title: "編集の前に、まず参考画像を読み解く",
        description:
          "プリセットは固定された仕上がりを与えるだけです。PhotoScope は参考画像の低い彩度、柔らかなハイライト、暖色と寒色の関係、視覚的な構造を読み解き、固定のレシピではなく、応用できるスタイルの規則を学べるようにします。",
      },
      gapVisualization: {
        title: "データがこの写真に何を意味するか、見て取る",
        description:
          "専門的なグラフはデータを示せます。PhotoScope はさらに、あなたの写真と参考画像を同じ文脈で比較します。数値が違うことだけでなく、その差がどの領域にあり、画面の印象にどう影響するのかまで見えるようになります。",
      },
      aiInsights: {
        title: "一般論ではなく、この写真の証拠で導く",
        description:
          "一般的なチュートリアルは一つの方法を教えます。PhotoScope は今回の比較から決定的な差を見つけ、その視覚的な影響を説明し、優先順位をつけた調整を示すので、どの一手にも画面上の根拠があります。",
      },
      styleAdaptation: {
        title: "参考画像に近づけつつ、写真そのものも残す",
        description:
          "機械的な適用は、元画像の光・被写体・色の土台を見落としがちです。PhotoScope は移すべきスタイルの要素を選ぶ手助けをしつつ、すでに成立している部分を保つので、仕上がりはやはりあなたの作品のままです。",
      },
    },
  },
};
